import { Component, forwardRef, EventEmitter, ElementRef, ViewChild,
  ViewEncapsulation, HostBinding } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, NgModel, FormGroup } from '@angular/forms';
import { Input, Output } from '@angular/core';
import { Observable } from 'rxjs';
import { Utils } from './utils.service';
import { FormValidationService } from './form-validation.service';
// TODO: ld-tooltip will be replaced by ula-tooltip; this is a breaking change
import { TooltipContentComponent } from '../tooltip';
import { IMyOptions } from 'mydatepicker';
import { maskedNumberInputShiftWorkaround } from '../../utils/masked-number-input-shift-workaround';

const noop = () => {};
export const ULA_DATE_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => UlaDateInputComponent),
  multi: true
};


let nextUniqueId = 0;


@Component({
  selector: 'ula-date-input',
  templateUrl: './ula-date-input.template.html',
  styleUrls: ['./ula-input.style.css'],
  providers: [ULA_DATE_INPUT_CONTROL_VALUE_ACCESSOR],
  encapsulation: ViewEncapsulation.None
})
export class UlaDateInputComponent implements ControlValueAccessor {
  public counter: number = nextUniqueId++;
  public _valid = true;
  public _readonly = false;

  // omitting the '_class' variable name doesn't seem to correctly apply the CSS class,
  // so don't omit it
  @HostBinding('class') _class = 'd-block';

  @ViewChild('input') input: ElementRef;
  @ViewChild('inputModel') inputModel: NgModel;

  public _focused = false;
  public _value: any = '';
  public _elementType: 'input' | 'textarea';

  private _onTouchedCallback: () => void = noop;
  private _onChangeCallback: (_: any) => void = noop;

  @Input() validationForm: FormGroup;
  @Input() displayName: string;
  @Input() formControlName: string;
  @Input() shouldCheckIsDirty = false;
  @Input() hideError = false;

  /** Readonly properties. */
  get empty() { return (this._value == null || this._value === '') && this.type !== 'date'; }

  get inputName(): string { return `${this.name}${this.counter}`; }

  /**
   * Misc
   */
  @Input() title = '';
  @Input() labelPrefix: string;
  @Input() label: string;
  @Input() defaultTitle = '';
  @Input() popover: string | TooltipContentComponent;
  @Input() titleIconBefore: string = null;
  @Input() titleIconAfter: string = null;
  @Input() autofocus = false;

  /**
   * Bindings.
   */
  @Input() max: string | number = null;
  @Input() maxlength: number = null;
  @Input() min: string | number = null;
  @Input() minlength: number = null;
  @Input() placeholder = '';
  @Input() step: number = null;
  @Input() type = 'date';
  @Input() name: string = null;
  @Input() qa: string;
  @Input() mask: string = null;

  private _disabled = false;
  private _required = false;
  private _spellcheck = false;

  private dateSeparator = '/';
  private myDatePickerOptions: IMyOptions = {
    alignSelectorRight: true,
    dateFormat: 'mm/dd/yyyy',
    showInputField: false,
    editableDateField: false,
    height: '20px'
  };

  @Input()
  get disabled(): boolean { return this._disabled; }
  set disabled(value) { this._disabled = this.coerceBooleanProperty(value); }

  @Input()
  get required(): boolean { return this._required; }
  set required(value) { this._required = this.coerceBooleanProperty(value); }

  @Input()
  get spellcheck(): boolean { return this._spellcheck; }
  set spellcheck(value) { this._spellcheck = this.coerceBooleanProperty(value); }

  @Input()
  get readonly(): boolean { return this._readonly; }
  set readonly(value) { this._readonly = this.coerceBooleanProperty(value); }

  private _blurEmitter: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
  private _focusEmitter: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();

  @Output('blur')
  get onBlur(): Observable<FocusEvent> { return this._blurEmitter.asObservable(); }

  @Output('focus')
  get onFocus(): Observable<FocusEvent> { return this._focusEmitter.asObservable(); }

  @Output('dateChanged') dateChanged: EventEmitter<any> = new EventEmitter<any>();

  get value(): any { return this._value; }

  @Input() set value(v: any) {
    // If the new value was a deletion of a slash character, delete the slash and the leading digit.
    if (v.length === 2 && this._value.length === 3) {
      v = v.substring(0, 1);
    }

    if (v.length === 5 && this._value.length === 6) {
      v = v.substring(0, 4);
    }

    // Add the date separator character after each segment.
    const digitsOnly = Utils.getOnlyDigitsFromString(v);
    if ((digitsOnly.length === 2 && v.length === 2) || (digitsOnly.length === 4 && v.length === 5)) {
      v += this.dateSeparator;
    }

    if (v !== this._value) {
      this._value = v;
      this._onChangeCallback(v);
      this.validationForm.controls[this.formControlName].setValue(v);

      // Only emit date changed when a legit date was entered, or the field is emptied.
      if (digitsOnly.length === 8 || digitsOnly.length === 0) {
        this.dateChanged.emit({
          target: {
            value: v
          }
        });
      }
    }
  }

  constructor(elementRef: ElementRef,
              private formValidationService: FormValidationService,
              private utils: Utils) {
    this._elementType = elementRef.nativeElement.nodeName.toLowerCase() === 'ld-textarea' ? 'textarea' : 'input';
  }

  hasErrors() {
    return this.validationForm &&
      this.validationForm.controls[this.formControlName].errors &&
      (this.validationForm.controls[this.formControlName].touched ||
      !this.shouldCheckIsDirty) &&
      !this._focused;
  }

  _handleInput() {
    const el = this.input.nativeElement;

    maskedNumberInputShiftWorkaround(el, /\d/g);

    // don't allow more than 8 numberic digits (mmddyyyy) or more tha 10 overall (mm/dd/yyyy)
    while (Utils.getOnlyDigitsFromString(el.value).length > 8 || el.value.length > 10) {
      el.value = el.value.substr(0, el.value.length - 1);
    }

    const approved = (el.value.match(/[0-9\/]/g) || ['']).join('');
    if (approved !== el.value) {
      el.value = approved;
    }
  }

  _handleKeyPress(event: KeyboardEvent) {
    const pressedKey = String.fromCharCode(event.which);

    // Need this for backspace to function correctly in Firefox
    if (event.key === 'Backspace') {
      return true;
    }

    // Need this for Tab to function correctly in Firefox
    if (event.key === 'Tab') {
      return true;
    }

    const digitsOnly = Utils.getOnlyDigitsFromString(this.input.nativeElement.value);
    if (!/[0-9]/.test(pressedKey)) {
      if (pressedKey === this.dateSeparator) {

        // If the user enters the date separator when there is one digit present, assume he means a month or date in the single digits.
        if (digitsOnly.length === 1 || digitsOnly.length === 3) {
          this.value = digitsOnly.substring(0, digitsOnly.length - 1) + '0' + digitsOnly.substring(digitsOnly.length - 1);
        }
      }

      return false;
    }

    // Assume the user is entering a single digit month, if he enters something above 1 with no other digits present.
    const numericValue = (+pressedKey);
    if (digitsOnly.length === 0 && numericValue > 1) {
      this.value = '0' + pressedKey + this.dateSeparator;

      return false;
    }

    // Prevent entering '00' for the month.
    if (digitsOnly.length === 1 && (+digitsOnly) === 0 && numericValue === 0) {
      return false;
    }

    // Limit the months segment to 12.
    if (digitsOnly.length === 1 && numericValue > 2) {
      const numericInitialMonthValue = +(digitsOnly.charAt(0));
      if (numericInitialMonthValue === 1) {
        this.value = digitsOnly + '2';

        return false;
      }
    }

    // If the first digit he enters in the days segment is above 3, assume he means a single digit day in that month.
    if (digitsOnly.length === 2 && numericValue > 3) {
      this.value = this.input.nativeElement.value + '0' + pressedKey + this.dateSeparator;

      return false;
    }

    // Prevent entering '00' in the days segment.
    if (digitsOnly.length === 3 && (+(digitsOnly.charAt(2))) === 0 && numericValue === 0) {
      return false;
    }

    // Limit the days segment to 31.
    if (digitsOnly.length === 3 && numericValue > 1) {
      const numericInitialDayValue = +(digitsOnly.charAt(2));
      if (numericInitialDayValue === 3) {
        this.value = this.input.nativeElement.value + '1' + this.dateSeparator;

        return false;
      }
    }

    // If there are enough digits to make mm/dd/yyyy, prevent further digits from being entered.
    if (digitsOnly.length === 8 && /[0-9]/.test(pressedKey)) {
      return false;
    }

    return true;
  }

  _handleFocus(event: FocusEvent) {
    this._focused = true;
    this._focusEmitter.emit(event);
  }

  _handleBlur(event: FocusEvent) {
    this._focused = false;
    this._onTouchedCallback();
    this._blurEmitter.emit(event);
  }

  _handleChange(event: Event) {
    this.value = (<HTMLInputElement>event.target).value;
    this._onTouchedCallback();
  }

  _handleDateChange(event) {
    this.value = event.formatted;
    this._onTouchedCallback();
  }

  writeValue(value: any) {
    this._value = value;
  }

  registerOnChange(fn: any) {
    this._onChangeCallback = fn;
  }

  registerOnTouched(fn: any) {
    this._onTouchedCallback = fn;
  }

  coerceBooleanProperty(value: any): boolean {
    return value != null && `${value}` !== 'false';
  }

  /**
   * Convert the value passed in to a value that is expected from the type of the md-input.
   * This is normally performed by the *_VALUE_ACCESSOR in forms, but since the type is bound
   * on our internal input it won't work locally.
   * @private
   */
  private _convertValueForInputType(v: any): any {
    let returnValue = v;
    switch (this.type) {
      case 'number':
        returnValue = this.utils.convertNumberValue(v);
        break;
      default:
        break;
    }

    return returnValue;
  }
}
